import React, { useState, useEffect } from "react";
import {
    Search,
    Download,
    ChevronLeft,
    ChevronRight,
    Eye,
    CheckCircle2,
    Loader2,
    AlertCircle,
    XCircle
} from "lucide-react";
import { useLogs } from "../../../../hooks/useLogs";
import { BASE_URL } from "../../../../constant/constant";
import { exportLogs } from "../../../../services/adminService";
import LogDetailModal from "./LogDetailModal";
import APIUsageOverview from "./overview/APIUsageOverview";

const APIUsageDetail = ({ onBack, userData }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [endpointFilter, setEndpointFilter] = useState("all");
    const [apiTypeFilter, setApiTypeFilter] = useState("all");
    const [languageFilter, setLanguageFilter] = useState("all");
    const [timeframeFilter, setTimeframeFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const {
        logs,
        pagination,
        loading
    } = useLogs({
        userId: userData?.id || userData?._id,
        page: currentPage,
        limit: 10,
        status: statusFilter,
        endpoint: endpointFilter,
        apiType: apiTypeFilter,
        language: languageFilter,
        timeframe: timeframeFilter,
        search: debouncedSearchTerm
    });

    const getStatusIcon = (status) => {
        const lowerStatus = String(status).toLowerCase();
        if (lowerStatus === 'success' || (Number(status) >= 200 && Number(status) < 300))
            return <CheckCircle2 className="w-4 h-4 text-green-500" />;
        if (lowerStatus === 'warning' || Number(status) === 429)
            return <AlertCircle className="w-4 h-4 text-orange-400" />;
        return <XCircle className="w-4 h-4 text-red-500" />;
    };

    const renderApiTypeBadge = (log) => {
        const rawType = log?.type || log?.successDetails?.apiType || '';
        const endpoint = log?.endpoint || log?.successDetails?.apiEndpoint || '';

        let badgeText = 'TTT';
        let bgClass = 'bg-[#E8F8F0]';
        let textClass = 'text-[#1C5D42]';

        const lowerType = rawType.toLowerCase();
        const lowerEndpoint = endpoint.toLowerCase();

        if (lowerType.includes('text to speech') || lowerType === 'tts' || lowerEndpoint.includes('/tts')) {
            badgeText = 'TTS';
            bgClass = 'bg-[#FDF8E7]';
            textClass = 'text-[#B59928]';
        } else if (lowerType.includes('speech to text') || lowerType === 'stt' || lowerEndpoint.includes('/stt')) {
            badgeText = 'STT';
            bgClass = 'bg-[#E6F4EA]';
            textClass = 'text-[#10B981]';
        } else if (lowerType.includes('speech translation') || lowerType === 'sts' || lowerEndpoint.includes('/st/')) {
            badgeText = 'STS';
            bgClass = 'bg-[#FFF0E6]';
            textClass = 'text-[#F97316]';
        } else if (lowerType.includes('text-to-speech translation') || lowerType === 'ttst') {
            badgeText = 'TTST';
            bgClass = 'bg-[#EEF2FF]';
            textClass = 'text-[#5A67D8]';
        } else if (lowerType.includes('speech-to-text translation') || lowerType === 'sttt') {
            badgeText = 'STTT';
            bgClass = 'bg-[#FFE8E8]';
            textClass = 'text-[#FF5A5F]';
        }

        return (
            <span className={`px-2.5 py-1 text-xs font-bold rounded-lg border border-gray-100 ${bgClass} ${textClass}`}>
                {badgeText}
            </span>
        );
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleApiTypeChange = (e) => {
        setApiTypeFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleEndpointChange = (e) => {
        setEndpointFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleLanguageChange = (e) => {
        setLanguageFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleTimeframeChange = (e) => {
        setTimeframeFilter(e.target.value === "All Time" ? "all" : e.target.value);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setSearchTerm("");
        setStatusFilter("all");
        setEndpointFilter("all");
        setApiTypeFilter("all");
        setLanguageFilter("all");
        setTimeframeFilter("all");
        setCurrentPage(1);
    };

    const hasActiveFilters =
        searchTerm !== "" ||
        statusFilter !== "all" ||
        endpointFilter !== "all" ||
        apiTypeFilter !== "all" ||
        languageFilter !== "all" ||
        timeframeFilter !== "all";

    const handleExport = async () => {
        try {
            setExporting(true);
            await exportLogs({
                userId: userData?.id || userData?._id,
                status: statusFilter !== "all" ? statusFilter : undefined,
                endpoint: endpointFilter !== "all" ? endpointFilter : undefined,
                apiType: apiTypeFilter !== "all" ? apiTypeFilter : undefined,
                language: languageFilter !== "all" ? languageFilter : undefined,
                timeframe: timeframeFilter !== "all" ? timeframeFilter : undefined,
                search: searchTerm || undefined
            });
        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* User Header Section */}
            <div className="bg-[#F8FAFA] rounded-2xl p-4 sm:p-6 border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    {userData?.profilePicture ? (
                        <img
                            src={userData.profilePicture.startsWith('http') ? userData.profilePicture : `${BASE_URL}${userData.profilePicture}`}
                            alt={userData.name}
                            className="w-8 h-8 rounded-full border border-gray-100"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-400">
                            {userData?.name?.charAt(0) || '?'}
                        </div>
                    )}
                    <div className="text-center sm:text-left">
                        <h2 className="text-xl font-bold text-gray-900">{userData?.name || "User"}</h2>
                        <p className="text-sm text-gray-500 font-medium">{userData?.email || "No email provided"}</p>
                    </div>
                </div>
                <button
                    onClick={onBack}
                    className="px-8 py-2.5 bg-[#075038] text-white rounded-xl hover:bg-[#075038]/90 transition-all font-bold text-sm"
                >
                    Done
                </button>
            </div>

            {/* Single User Analytics Overview */}
            <APIUsageOverview userId={userData?.id || userData?._id} />

            {/* API Call Section Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">API Calls</h3>
                </div>
                <button
                    onClick={handleExport}
                    disabled={exporting}
                    className="flex items-center justify-center gap-2 px-6 py-2 bg-[#075038] text-white rounded-xl hover:bg-[#075038]/90 transition-all font-medium text-sm disabled:opacity-50"
                >
                    {exporting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Download className="w-4 h-4" />
                    )}
                    {exporting ? "Exporting..." : "Export User Logs"}
                </button>
            </div>

            {/* Filters Section */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                    <div className="w-full sm:flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by endpoint, status..."
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#075038] focus:border-transparent outline-none transition-all text-sm"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                    {hasActiveFilters && (
                        <button
                            onClick={handleResetFilters}
                            className="px-4 py-2.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all whitespace-nowrap"
                        >
                            Reset Filters
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {/* Status Filter */}
                    <div className="relative">
                        <select
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#075038] focus:border-transparent outline-none transition-all text-sm appearance-none cursor-pointer capitalize"
                            value={statusFilter}
                            onChange={handleStatusChange}
                        >
                            <option value="all">All Status</option>
                            <option value="success">Success</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                    </div>

                    {/* API Type Filter */}
                    <div className="relative">
                        <select
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#075038] focus:border-transparent outline-none transition-all text-sm appearance-none cursor-pointer"
                            value={apiTypeFilter}
                            onChange={handleApiTypeChange}
                        >
                            <option value="all">All API Types</option>
                            <option value="Text-To-Text">Text-To-Text</option>
                            <option value="Text-To-Speech">Text-To-Speech</option>
                            <option value="Speech-To-Text">Speech-To-Text</option>
                            <option value="Speech Translations">Speech Translations</option>
                            <option value="Text-To-Speech Translations">Text-To-Speech Translations</option>
                            <option value="Speech-To-Text Translation">Speech-To-Text Translation</option>
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                    </div>

                    {/* Endpoint Filter */}
                    <div className="relative">
                        <select
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#075038] focus:border-transparent outline-none transition-all text-sm appearance-none cursor-pointer"
                            value={endpointFilter}
                            onChange={handleEndpointChange}
                        >
                            <option value="all">All Endpoints</option>
                            <option>/translate/english_to_yoruba</option>
                            <option>/translate/yoruba_to_english</option>
                            <option>/translate/english_to_swahili</option>
                            <option>/translate/swahili_to_english</option>
                            <option>/translate/english_to_pidgin</option>
                            <option>/translate/pidgin_to_english</option>
                            <option>/tts</option>
                            <option>/stt</option>
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                    </div>

                    {/* Language Filter */}
                    <div className="relative">
                        <select
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#075038] focus:border-transparent outline-none transition-all text-sm appearance-none cursor-pointer"
                            value={languageFilter}
                            onChange={handleLanguageChange}
                        >
                            <option value="all">All Languages</option>
                            <option value="Yoruba">Yoruba</option>
                            <option value="Swahili">Swahili</option>
                            <option value="Nigerian Pidgin">Nigerian Pidgin</option>
                            <option value="English">English</option>
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                    </div>

                    {/* Timeframe Filter */}
                    <div className="relative">
                        <select
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#075038] focus:border-transparent outline-none transition-all text-sm appearance-none cursor-pointer"
                            value={timeframeFilter}
                            onChange={handleTimeframeChange}
                        >
                            <option value="all">All Time</option>
                            <option value="24h">Last 24 Hours</option>
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                            <option value="90d">Last 90 Days</option>
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                    </div>
                </div>
            </div>

            {/* Info Line */}
            <div className="flex items-center justify-between text-sm">
                <p className="text-gray-500 font-medium">Showing {logs.length} of {pagination.total || 0} API Call{pagination.total !== 1 ? 's' : ''}</p>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 cursor-default group">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-gray-600 transition-colors group-hover:text-gray-900">Success</span>
                    </div>
                    <div className="flex items-center gap-1.5 cursor-default group">
                        <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                        <span className="text-gray-600 transition-colors group-hover:text-gray-900">Warning</span>
                    </div>
                    <div className="flex items-center gap-1.5 cursor-default group">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="text-gray-600 transition-colors group-hover:text-gray-900">Error</span>
                    </div>
                </div>
            </div>

            {/* Details Table Section */}
            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50/50">
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100">No</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100 text-center">API</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100">Timestamp</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100">Endpoint</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100 text-center">Method</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100">Status</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100">Response Time</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100 text-right">Tokens</th>
                            <th className="px-6 py-4 text-sm font-semibold text-gray-600 border-b border-gray-100 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 font-medium">
                        {loading ? (
                            <tr>
                                <td colSpan="10" className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <Loader2 className="w-8 h-8 text-[#075038] animate-spin" />
                                        <p className="text-gray-500 text-sm font-medium">Loading details...</p>
                                    </div>
                                </td>
                            </tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan="10" className="px-6 py-12 text-center">
                                    <p className="text-gray-500 text-sm font-medium">No records found for this user</p>
                                </td>
                            </tr>
                        ) : (
                            logs.map((log, index) => (
                                <tr key={log._id} className="hover:bg-gray-50/50 transition-colors group">
                                    <td className="px-6 py-4 text-sm text-gray-600">{(currentPage - 1) * 10 + index + 1}</td>
                                    <td className="px-6 py-4 text-center">
                                        {renderApiTypeBadge(log)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 font-bold whitespace-nowrap">
                                        {new Date(log.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                                        {(log.endpoint || log?.successDetails?.apiEndpoint || '')
                                            .replace(/^https?:\/\/[^/]+/, '')
                                            .replace(/^\/ttt\/v1/, '')
                                            .replace(/^\/api\/v1/, '') || '/'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-center">
                                            <span className="px-3 py-1 text-xs font-bold bg-white border border-gray-200 rounded-md text-gray-700">
                                                POST
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {getStatusIcon(log.status)}
                                            <span className="text-sm text-gray-900 capitalize">{log.status}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {log.responseTime >= 1000 ? `${(log.responseTime / 1000).toFixed(2)}s` : `${log.responseTime}ms`}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold">{log.tokensUsed?.toLocaleString() || 0}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-center">
                                            <button
                                                onClick={() => {
                                                    setSelectedLog(log);
                                                    setIsModalOpen(true);
                                                }}
                                                className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg transition-all text-gray-900"
                                            >
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 text-sm pt-2">
                    <button
                        disabled={currentPage === 1 || loading}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all font-medium text-gray-700 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                        Previous
                    </button>
                    <div className="flex items-center gap-1 mx-1">
                        {(() => {
                            const total = pagination.totalPages || 0;
                            const current = currentPage;
                            let pages = [];

                            if (total <= 7) {
                                pages = Array.from({ length: total }, (_, i) => i + 1);
                            } else if (current <= 4) {
                                pages = [1, 2, 3, 4, 5, '...', total];
                            } else if (current >= total - 3) {
                                pages = [1, '...', total - 4, total - 3, total - 2, total - 1, total];
                            } else {
                                pages = [1, '...', current - 1, current, current + 1, '...', total];
                            }

                            return pages.map((page, idx) => (
                                typeof page === 'number' ? (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentPage(page)}
                                        className={`w-9 h-9 flex items-center justify-center rounded-xl font-semibold transition-all ${currentPage === page
                                                ? "bg-[#075038] text-white shadow-sm"
                                                : "hover:bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        {page}
                                    </button>
                                ) : (
                                    <span key={idx} className="w-8 h-9 flex items-center justify-center text-gray-400 font-medium">
                                        ...
                                    </span>
                                )
                            ));
                        })()}
                    </div>
                    <button
                        disabled={currentPage === pagination.totalPages || loading}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all font-medium text-gray-700 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                </div>
            )}

            <LogDetailModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                log={selectedLog}
            />
        </div>
    );
};

export default APIUsageDetail;
